// Killer's UI wiring. Unlike every other game here it has two screens before
// the match is actually decided - the shared mount() container starts on the
// number-assignment screen (each player throws in turn to claim a number, no
// duplicates, with its own undo), then swaps its own innerHTML over to the
// live board once everyone has one. Both screens share the same full-board
// dartboard input (components/dartboard-input.js's mountDartboardFullBoard),
// remounted fresh after every tap so its click handlers and claimed/dimmed
// wedges always match current state exactly - the same "just re-render"
// approach the rest of this controller uses instead of patching the DOM.
//
// Rules: each player has a fixed number of lives, tracked as a single
// number (`remaining`, starting at 0 - the life dots start empty, not
// full). Being "the Killer" isn't its own flag - it's simply true whenever
// remaining === lives, i.e. at full life, and false the instant it isn't.
// Whichever number gets hit (yours or someone else's), only one thing
// decides the outcome: whether the THROWER is currently a Killer. A
// Killer's dart removes life points from the number's owner (which can
// eliminate them, and can even be the Killer's own number). A non-Killer's
// dart instead ADDS life points to the number's owner, capped at `lives` -
// the ordinary way to build your own number up toward becoming a Killer,
// but if you tap someone else's number instead, that's life for them, not
// you. Either way, a single/double/triple is worth 1/2/3 life points. A
// player is only eliminated once pushed below zero - reaching exactly 0 is
// still one hit from surviving. Last player not eliminated wins.
import killer from './definition.js';
import { mountDartboardFullBoard, drawDartMark } from '../../components/dartboard-input.js';
import { configRowDefault } from '../shared/config-rows.js';
import { killerObserverBoard } from '../shared/killer-observer-board.js';

let currentPlayers = [];
let container = null;
let onChangeCallback = null;
let lives = configRowDefault(killer, 'killer-lives');
let includeBull = configRowDefault(killer, 'killer-include-bull');
let toastTimer = null;
let isObserver = false;
let currentStage = 'assign'; // 'assign' | 'board' - which screen is actually mounted right now

// --- number-assignment phase ---
let assignments = {}; // player -> number, only for players who've picked so far
let assignIndex = 0;
let assignHistory = []; // stack of {player, number}, for undo

// --- live-board phase ---
let state = {}; // player -> { number, remaining, eliminated } - isKiller isn't
                 // stored at all, just derived as `remaining === lives` (see isKiller())
let activeIndex = 0;
let dartsThisTurn = 0;
let turnDarts = []; // {number, mult} per dart thrown so far this turn, for the recent-darts row
let lastTurnDarts = []; // previous turn's darts, kept in the recent-darts row until the next player's first dart lands, instead of vanishing the instant the 3rd dart lands
let matchComplete = false;
let history = []; // stack of full-state snapshots, for undo
let renderedRemaining = {}; // player -> `remaining` as of the last renderAll(), for the life-dot pulse animation
// The most recent toast, carried in getState() so every synced client (an
// observer especially - it never runs handleGameDart/showToast itself) sees
// the same "X becomes a Killer!", "X eliminates Y!", etc. feedback, not just
// the client that threw the dart. `seq` is how setState() tells a genuinely
// new toast apart from the same one arriving again.
let toastSeq = 0;
let lastToast = null; // { msg, kind, seq } | null
let lastAppliedToastSeq = 0; // highest seq this client has already shown

function hasProgress() {
    return assignHistory.length > 0 || history.length > 0;
}

function numberLabel(num, short = false) {
    return num === 25 ? (short ? 'B' : 'Bull') : String(num);
}

// The recent-darts row uses the plain number even for bull (25/D25), rather
// than numberLabel()'s "Bull" wording used elsewhere on the board.
function dartThrowLabel(number, mult) {
    const prefix = mult === 2 ? 'D' : mult === 3 ? 'T' : '';
    return `${prefix}${number}`;
}

function showToast(msg, kind) {
    toastSeq += 1;
    lastToast = { msg, kind, seq: toastSeq };
    lastAppliedToastSeq = toastSeq; // showing it ourselves right now - nothing for setState() to replay later
    renderToast(msg, kind);
}

// The actual DOM update, split out from showToast() so a toast that arrived
// via setState() (someone else's dart, replayed for this client) can be
// rendered the same way without re-triggering toastSeq/lastToast bookkeeping
// meant for this client's own actions.
function renderToast(msg, kind) {
    const el = document.getElementById('killerToast');
    if (!el) return;
    el.textContent = msg;
    el.className = 'killer-toast show ' + (kind || '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 2000);
}

function hideToast() {
    const el = document.getElementById('killerToast');
    if (el) el.classList.remove('show');
    clearTimeout(toastTimer);
}

// Dims and disables whichever wedges are already claimed - the same
// dim-everything-but-what's-relevant technique mountDartboardForTarget uses,
// just applied to a caller-supplied set of numbers on the full board instead
// of "everything except one target".
function dimClaimedNumbers(mount, claimedNumbers) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    claimedNumbers.forEach(num => {
        if (num === 25) {
            ['.outer-bull', '.bullseye'].forEach(sel => {
                const el = svg.querySelector(sel);
                if (el) { el.style.opacity = '0.3'; el.style.pointerEvents = 'none'; }
            });
            return;
        }
        const section = svg.querySelector(`g.section-${num}`);
        if (section) { section.style.opacity = '0.3'; section.style.pointerEvents = 'none'; }
        svg.querySelectorAll('text').forEach(label => {
            if (label.textContent.trim() === String(num)) label.style.opacity = '0.3';
        });
    });
}

// A real dartboard's wedges, in clockwise order starting from the top -
// dartboard.svg's own .section-divider lines are authored in this exact
// order too (divider i sits between WEDGE_ORDER[i-1] and WEDGE_ORDER[i]),
// which is what lets dimUnassignedNumbers below match each divider to the
// two numbers it separates without any per-divider geometry of its own.
const WEDGE_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];

// Dims every number nobody has claimed - only the numbers actually assigned
// to a player are ever worth throwing at once the game's live, the same
// dim-everything-but-what's-relevant idea Halve It's own dartboard input
// uses for its single active target. Left clickable (unlike
// dimClaimedNumbers above) since tapping one still means something - "No
// one owns it" - just a harmless one.
function dimUnassignedNumbers(mount, assignedNumbers) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    const assigned = new Set(assignedNumbers);
    svg.querySelectorAll('g[class^="section-"]').forEach(section => {
        const match = /section-(\d+)/.exec(section.getAttribute('class') || '');
        if (!match) return;
        if (!assigned.has(Number(match[1]))) section.style.opacity = '0.3';
    });
    if (!assigned.has(25)) {
        ['.outer-bull', '.bullseye'].forEach(sel => {
            const el = svg.querySelector(sel);
            if (el) el.style.opacity = '0.3';
        });
    }
    svg.querySelectorAll('text').forEach(label => {
        const num = Number(label.textContent.trim());
        if (!Number.isNaN(num) && !assigned.has(num)) label.style.opacity = '0.3';
    });

    // A divider between two dimmed (unassigned) wedges is just as
    // irrelevant as they are - only keep one at full brightness if at
    // least one of its two neighbors is actually in play.
    const dividers = svg.querySelectorAll('.section-divider');
    if (dividers.length === WEDGE_ORDER.length) {
        dividers.forEach((line, i) => {
            const a = WEDGE_ORDER[(i - 1 + WEDGE_ORDER.length) % WEDGE_ORDER.length];
            const b = WEDGE_ORDER[i];
            line.style.opacity = (assigned.has(a) || assigned.has(b)) ? '' : '0.3';
        });
    }
}

// Pulses the wedge(s) the active player should go for - their own number
// while they're not yet a Killer (building toward it), or every alive
// opponent's number once they are one. Black/red wedges (dartboard.svg
// pairs #2A2A2A singles with #CC0000 rings, same as the red .bullseye)
// start so much darker than their white/green counterparts that the same
// brightness multiplier barely reads on them - killer-target-wedge-dark
// pulses harder to look equally lit (see styles.css).
function highlightTargetNumbers(mount, targetNumbers) {
    const svg = mount.querySelector('svg');
    if (!svg) return;
    const targets = new Set(targetNumbers);
    svg.querySelectorAll('g[class^="section-"]').forEach(section => {
        const match = /section-(\d+)/.exec(section.getAttribute('class') || '');
        if (!match) return;
        const isTarget = targets.has(Number(match[1]));
        const isDark = section.querySelector('.single')?.getAttribute('fill') === '#2A2A2A';
        section.classList.toggle('killer-target-wedge', isTarget && !isDark);
        section.classList.toggle('killer-target-wedge-dark', isTarget && isDark);
    });
    const wantsBull = targets.has(25);
    const outerBull = svg.querySelector('.outer-bull'); // green
    if (outerBull) outerBull.classList.toggle('killer-target-wedge', wantsBull);
    const bullseye = svg.querySelector('.bullseye'); // red
    if (bullseye) bullseye.classList.toggle('killer-target-wedge-dark', wantsBull);
}

// --- number-assignment phase ---

function assignScreenHTML(players, observer) {
    // An observer sees the same dartboard (dimmed/undimmed exactly the same
    // way, read-only - see renderAssignScreen) but never gets the Undo/Start
    // buttons - there's nothing for it to drive there.
    return `
        <div class="killer-assign">
            <div class="killer-assign-header">
                <div class="killer-step">Choosing numbers &middot; player <b id="killerAssignStepNum">1</b> of <b>${players.length}</b></div>
                <h2 class="killer-turn-title"><b id="killerAssignPlayerName"></b></h2>
                <p class="killer-turn-hint" id="killerAssignHint">Tap a wedge on the board to claim that number</p>
            </div>
            <div class="killer-claimed-strip" id="killerClaimedStrip"></div>
            <div class="killer-assign-board-area">
                <div class="dartboard-mount killer-dart-mount" id="killerAssignDartMount"></div>
            </div>
            ${observer ? '' : `
            <div class="killer-assign-footer">
                <button class="grid-icon-btn grid-icon-btn-killer" type="button" id="killerAssignUndoBtn" disabled title="Undo last pick" aria-label="Undo last pick">
                    <i class="fa-solid fa-rotate-left"></i>
                </button>
                <button class="btn btn-large" type="button" id="killerAssignStartBtn" disabled>Start Game</button>
            </div>`}
        </div>
    `;
}

function notifyAssignChange() {
    if (!onChangeCallback) return;
    onChangeCallback({ scores: {}, complete: false });
}

function handleAssignTap(number) {
    if (assignIndex >= currentPlayers.length) return;
    if (number === 25 && includeBull !== 'yes') return; // bull excluded from this game - the dimmed wedge shouldn't fire anyway
    if (Object.values(assignments).includes(number)) return; // already claimed - the dimmed wedge shouldn't fire anyway

    const player = currentPlayers[assignIndex];
    assignments[player] = number;
    assignHistory.push({ player, number });
    assignIndex += 1;
    renderAssignScreen();
    notifyAssignChange();
}

function handleAssignUndo() {
    const last = assignHistory.pop();
    if (!last) return;
    delete assignments[last.player];
    assignIndex -= 1;
    renderAssignScreen();
    notifyAssignChange();
}

function renderAssignScreen() {
    const allAssigned = assignIndex >= currentPlayers.length;

    document.getElementById('killerAssignStepNum').textContent = Math.min(assignIndex + 1, currentPlayers.length);
    document.getElementById('killerAssignPlayerName').textContent = allAssigned
        ? 'Everyone has a number'
        : `${currentPlayers[assignIndex]}'s throw`;
    document.getElementById('killerAssignHint').textContent = allAssigned
        ? (isObserver ? '' : 'Press Start to begin the game')
        : (isObserver ? '' : 'Tap a wedge on the board to claim that number');

    document.getElementById('killerClaimedStrip').innerHTML = currentPlayers.map(player => {
        const num = assignments[player];
        const pending = num === undefined;
        return `
            <div class="killer-chip${pending ? ' pending' : ''}">
                <span class="killer-chip-num">${pending ? '?' : numberLabel(num, true)}</span>
                <span class="killer-chip-name">${player}</span>
            </div>
        `;
    }).join('');

    if (!isObserver) {
        document.getElementById('killerAssignUndoBtn').disabled = assignHistory.length === 0;
        document.getElementById('killerAssignStartBtn').disabled = !allAssigned;
    }

    const mount = document.getElementById('killerAssignDartMount');
    if (allAssigned) {
        mount.innerHTML = '';
        return;
    }
    mountDartboardFullBoard(mount, { onTap: (number) => handleAssignTap(number), readOnly: isObserver });
    // The bull is excluded from selection entirely when this game's
    // "Include Bull/Bullseye" config is No - dimmed and disabled exactly
    // like an already-claimed number, permanently for the whole assign
    // phase rather than just until someone picks it.
    const claimedForDim = includeBull === 'yes' ? Object.values(assignments) : [...Object.values(assignments), 25];
    dimClaimedNumbers(mount, claimedForDim);
}

// --- live-board phase ---

function boardScreenHTML(players, observer) {
    // The observer's wall display is a different layout altogether (see
    // renderObserverBoard), not the phone board minus its Undo button - it
    // only keeps the toast, which still carries "X eliminates Y!" feedback.
    if (observer) {
        return `
            <div class="obs-board obs-killer" id="killerObsBoard"></div>
            <div class="killer-toast" id="killerToast"></div>
        `;
    }
    const cards = players.map((player, i) => `
        <div class="killer-player" id="killer-player-${i}">
            <i class="fa-regular fa-circle-dot killer-turn-icon" id="killer-badge-${i}" style="visibility:hidden"></i>
            <div class="killer-number" id="killer-number-${i}"></div>
            <div class="killer-main">
                <div class="killer-name-row">
                    <span class="killer-name">${player}</span>
                    <span class="killer-badge" id="killer-killerbadge-${i}" style="display:none">Killer</span>
                    <span class="killer-out-badge" id="killer-outbadge-${i}" style="display:none">Out</span>
                </div>
                <div class="killer-lives" id="killer-lives-${i}"></div>
            </div>
        </div>
    `).join('');

    return `
        <div class="killer-board">
            <div class="killer-grid${players.length === 2 ? ' killer-grid-full-width' : ''}">${cards}</div>
            <div class="killer-board-header-row">
                <button class="grid-icon-btn-killer-pill killer-corner-undo" type="button" id="killerBoardUndoBtn" disabled title="Undo last dart" aria-label="Undo last dart">
                    <i class="fa-solid fa-rotate-left"></i> Undo
                </button>
                <div class="killer-board-header">
                    <div class="killer-turn-row" id="killerTurnRow"></div>
                </div>
            </div>
            <div class="killer-hint" id="killerHint"></div>
            <div class="dartboard-mount killer-dart-mount" id="killerBoardDartMount"></div>
            <div class="killer-recent-darts" id="killerRecentDarts"></div>
        </div>
        <div class="killer-toast" id="killerToast"></div>
    `;
}

// `prevRemaining` is the value this same row last rendered with - any dot
// whose filled/lost state differs between the two gets a one-shot pulse
// animation (see .killer-life-changed), so only the dots that actually
// gained or lost a life animate, not the whole row.
function livesDotsHTML(remaining, prevRemaining) {
    const lo = Math.max(0, Math.min(remaining, prevRemaining));
    const hi = Math.min(lives, Math.max(remaining, prevRemaining));
    let html = '';
    for (let i = 0; i < lives; i++) {
        const lost = i >= remaining;
        const changed = i >= lo && i < hi;
        html += `<span class="killer-life${lost ? ' lost' : ''}${changed ? ' killer-life-changed' : ''}"></span>`;
    }
    return html;
}

function ownerOf(number) {
    return currentPlayers.find(p => state[p].number === number) || null;
}

function isKiller(player) {
    return state[player].remaining === lives;
}

function checkWin() {
    const alive = currentPlayers.filter(p => !state[p].eliminated);
    if (alive.length <= 1) {
        matchComplete = true;
        if (alive.length === 1) showToast(`${alive[0]} wins!`, 'win');
    }
}

function advanceTurn() {
    // Kept around so the recent-darts row still shows the turn that just
    // ended - renderAll() only falls back to it while the new turn hasn't
    // thrown a dart of its own yet (turnDarts.length === 0), so it's
    // naturally superseded the moment the next player's first dart lands.
    lastTurnDarts = turnDarts;
    dartsThisTurn = 0;
    turnDarts = [];
    for (let i = 0; i < currentPlayers.length; i++) {
        activeIndex = (activeIndex + 1) % currentPlayers.length;
        if (!state[currentPlayers[activeIndex]].eliminated) break;
    }
}

function snapshotGame() {
    return JSON.parse(JSON.stringify({ state, activeIndex, dartsThisTurn, turnDarts, matchComplete }));
}

// The whole rules engine, really: whether this dart takes lives from
// `owner` or gives them lives depends only on the THROWER's own current
// status - a Killer's dart always costs the owner life points (which can be
// their own number, and can eliminate them); anyone else's dart always
// gains the owner life points instead, capped at `lives` (the normal way to
// build your own number up, but tapping someone ELSE's number while you're
// not a Killer just helps them, not you). Either way, a single/double/
// triple is worth 1/2/3 life points, same as it'd be worth as a score
// anywhere else on the board.
function applyThrow(thrower, owner, mult) {
    const ownNumber = owner === thrower;
    const p = state[owner];
    const pointsWord = mult === 1 ? 'a life' : `${mult} lives`;

    if (isKiller(thrower)) {
        const ownerWasKiller = isKiller(owner);
        p.remaining -= mult;
        if (p.remaining < 0) p.eliminated = true;
        const statusSuffix = !p.eliminated && ownerWasKiller ? ' and their Killer status' : '';
        showToast(
            p.eliminated
                ? (ownNumber ? `${thrower} hits their own number and is eliminated!` : `${thrower} eliminates ${owner}!`)
                : (ownNumber
                    ? `${thrower} hits their own number — loses ${pointsWord}${statusSuffix}!`
                    : `${thrower} hits ${owner}'s number — ${pointsWord} lost${statusSuffix}!`),
            'bust'
        );
        return;
    }

    // A non-Killer's dart on an opponent who's already at full life (i.e.
    // already a Killer) changes nothing - remaining was already capped at
    // `lives` before this hit, so it can't newly "become" one from it.
    const ownerAlreadyKiller = isKiller(owner);
    p.remaining = Math.min(lives, p.remaining + mult);
    if (ownerAlreadyKiller) {
        showToast(`${thrower} hits ${owner}'s number — ${owner} is already a Killer, no effect`, '');
    } else if (p.remaining === lives) {
        showToast(
            ownNumber ? `${thrower} becomes a Killer!` : `${thrower} hits ${owner}'s number — ${owner} becomes a Killer!`,
            'killer'
        );
    } else {
        showToast(
            ownNumber
                ? `${thrower} hits their own number (${p.remaining}/${lives})`
                : `${thrower} hits ${owner}'s number — ${owner} gains ${pointsWord} (${p.remaining}/${lives})`,
            ''
        );
    }
}

function handleGameDart(number, mult, point) {
    if (matchComplete) return;
    const activePlayer = currentPlayers[activeIndex];
    history.push(snapshotGame());

    const owner = ownerOf(number);

    if (!owner) {
        showToast(`No one owns ${numberLabel(number)}`, '');
    } else if (state[owner].eliminated) {
        // Already out - nothing left to hit, and re-applying damage to an
        // already-negative `remaining` would keep re-triggering the
        // eliminates-them toast on every later hit, not just the first.
        showToast(`${owner} is already eliminated`, '');
    } else {
        applyThrow(activePlayer, owner, mult);
    }

    turnDarts.push({ number, mult, x: point && point.x, y: point && point.y });
    dartsThisTurn += 1;
    checkWin();
    if (!matchComplete && dartsThisTurn >= 3) advanceTurn();

    renderAll();
    notifyChange();
}

function undoLastDart() {
    const snap = history.pop();
    if (!snap) return;
    state = snap.state;
    activeIndex = snap.activeIndex;
    dartsThisTurn = snap.dartsThisTurn;
    turnDarts = snap.turnDarts;
    matchComplete = snap.matchComplete;
    // turnDarts is now restored from the snapshot, so lastTurnDarts (the
    // prior turn's darts) is no longer relevant - drop it so it can't
    // resurface if this restored turn is itself later undone back to empty.
    lastTurnDarts = [];
    hideToast();
    renderAll();
    // Undo must never be what finishes the match - it's a correction, not a
    // dart (matchComplete is always false in a restored snapshot anyway,
    // since one can't be pushed after the match already ended).
    notifyChange({ suppressComplete: true });
}

// The observer's wall display (a tablet a few metres from the players), laid
// out by games/shared/killer-observer-board.js. Rebuilt wholesale on every
// render - a handful of rows is cheap, and rebuilding is what replays the
// life-dot pulse (see livesDotsHTML) on just the dots that changed. Any dart
// counts in this game (no required ring), so targets are plain numbers.
function renderObserverBoard() {
    const board = document.getElementById('killerObsBoard');
    if (!board) return;

    const active = currentPlayers[activeIndex];
    const killerActive = isKiller(active);
    const { html, classes } = killerObserverBoard({
        players: currentPlayers,
        state,
        activeIndex,
        matchComplete,
        title: 'Cricket-style Killer',
        subtitle: `${lives} lives${includeBull === 'yes' ? ' &middot; bull' : ''}`,
        isKiller,
        numberLabel: number => numberLabel(number, true),
        targetsHeading: killerActive ? 'Kill with' : 'Build lives with',
        targetLabels: killerActive
            ? currentPlayers.filter(p => p !== active && !state[p].eliminated).map(p => numberLabel(state[p].number))
            : [numberLabel(state[active].number)],
        dartLabels: (turnDarts.length ? turnDarts : lastTurnDarts).map(({ number, mult }) => dartThrowLabel(number, mult)),
        noteFor: player => state[player].eliminated
            ? 'Eliminated'
            : isKiller(player)
                ? `Every hit on <b>${numberLabel(state[player].number)}</b> costs lives`
                : `Hit <b>${numberLabel(state[player].number)}</b> to build lives`,
        livesHTML: player => livesDotsHTML(state[player].remaining, renderedRemaining[player] ?? 0)
    });
    Object.entries(classes).forEach(([name, on]) => board.classList.toggle(name, on));
    board.innerHTML = html;
    currentPlayers.forEach(player => { renderedRemaining[player] = state[player].remaining; });
}

function renderAll() {
    if (isObserver) {
        renderObserverBoard();
        return;
    }
    currentPlayers.forEach((player, i) => {
        const p = state[player];
        const isTurn = i === activeIndex && !matchComplete;

        const numEl = document.getElementById(`killer-number-${i}`);
        if (numEl) numEl.textContent = numberLabel(p.number, true);

        const killerNow = isKiller(player);

        const card = document.getElementById(`killer-player-${i}`);
        if (card) {
            card.classList.toggle('active', isTurn);
            card.classList.toggle('out', p.eliminated);
        }

        // Killer status is shown on the number circle's color and the
        // badge below - not the whole card border, which would otherwise
        // compete with the active-turn border for attention.
        if (numEl) numEl.classList.toggle('killer', killerNow);

        const badge = document.getElementById(`killer-badge-${i}`);
        if (badge) badge.style.visibility = isTurn ? 'visible' : 'hidden';

        const killerBadge = document.getElementById(`killer-killerbadge-${i}`);
        if (killerBadge) killerBadge.style.display = killerNow ? 'inline-block' : 'none';

        const outBadge = document.getElementById(`killer-outbadge-${i}`);
        if (outBadge) outBadge.style.display = p.eliminated ? 'inline-block' : 'none';

        const livesEl = document.getElementById(`killer-lives-${i}`);
        if (livesEl) livesEl.innerHTML = livesDotsHTML(p.remaining, renderedRemaining[player] ?? 0);
        renderedRemaining[player] = p.remaining;
    });

    const undoBtn = document.getElementById('killerBoardUndoBtn');
    if (undoBtn) undoBtn.disabled = history.length === 0;

    const turnRow = document.getElementById('killerTurnRow');
    if (turnRow) {
        turnRow.innerHTML = matchComplete
            ? 'Game over'
            : `<b>${currentPlayers[activeIndex]}</b>'s throw &middot; dart <b>${dartsThisTurn + 1}</b> of 3`;
    }

    const hint = document.getElementById('killerHint');
    if (hint) hint.textContent = matchComplete ? '' : (isObserver ? '' : 'Tap the dartboard for this throw');

    const dartMount = document.getElementById('killerBoardDartMount');
    if (dartMount) {
        if (matchComplete) {
            dartMount.innerHTML = '';
        } else {
            // Remounted fresh on every dart (see file-top comment), which
            // wipes any dot drawn on the previous mount - replaying every
            // dart already thrown this turn (reset on undo/a new turn along
            // with turnDarts itself) puts them all back immediately.
            mountDartboardFullBoard(dartMount, { onTap: handleGameDart, readOnly: isObserver });
            dimUnassignedNumbers(dartMount, currentPlayers.filter(p => !state[p].eliminated).map(p => state[p].number));
            const activePlayer = currentPlayers[activeIndex];
            const targetNumbers = isKiller(activePlayer)
                ? currentPlayers.filter(p => p !== activePlayer && !state[p].eliminated).map(p => state[p].number)
                : [state[activePlayer].number];
            highlightTargetNumbers(dartMount, targetNumbers);
            turnDarts.forEach(({ x, y }) => {
                if (x !== undefined) drawDartMark(dartMount, x, y);
            });
        }
    }

    const recentDarts = document.getElementById('killerRecentDarts');
    if (recentDarts) {
        const dartsToShow = turnDarts.length ? turnDarts : lastTurnDarts;
        recentDarts.innerHTML = dartsToShow
            .map(({ number, mult }) => `<span>${dartThrowLabel(number, mult)}</span>`)
            .join(' - ');
    }
}

function notifyChange({ suppressComplete = false } = {}) {
    if (!onChangeCallback) return;
    const scores = {};
    const standingLabels = {};
    currentPlayers.forEach(player => {
        scores[player] = state[player].remaining;
        standingLabels[player] = isKiller(player) ? 'Killer' : '';
    });
    onChangeCallback({ scores, complete: !suppressComplete && matchComplete, standingLabels });
}

function beginGamePhase() {
    state = {};
    currentPlayers.forEach(player => {
        state[player] = { number: assignments[player], remaining: 0, eliminated: false };
    });
    activeIndex = 0;
    dartsThisTurn = 0;
    turnDarts = [];
    lastTurnDarts = [];
    matchComplete = false;
    history = [];
    renderedRemaining = {};
    currentStage = 'board';
    toastSeq = 0;
    lastToast = null;
    lastAppliedToastSeq = 0;

    container.innerHTML = boardScreenHTML(currentPlayers, isObserver);
    if (!isObserver) document.getElementById('killerBoardUndoBtn').addEventListener('click', undoLastDart);
    renderAll();
    notifyChange();
}

function getState() {
    return JSON.parse(JSON.stringify(
        currentStage === 'assign'
            ? { stage: 'assign', assignments, assignHistory, assignIndex }
            : { stage: 'board', state, activeIndex, dartsThisTurn, turnDarts, lastTurnDarts, matchComplete, lastToast }
    ));
}

function setState(snapshot) {
    if (snapshot.stage === 'assign') {
        assignments = snapshot.assignments;
        assignHistory = snapshot.assignHistory;
        assignIndex = snapshot.assignIndex;
        if (currentStage !== 'assign') {
            currentStage = 'assign';
            container.innerHTML = assignScreenHTML(currentPlayers, isObserver);
            if (!isObserver) {
                document.getElementById('killerAssignUndoBtn').addEventListener('click', handleAssignUndo);
                document.getElementById('killerAssignStartBtn').addEventListener('click', beginGamePhase);
            }
        }
        renderAssignScreen();
        return;
    }

    if (currentStage !== 'board') {
        currentStage = 'board';
        container.innerHTML = boardScreenHTML(currentPlayers, isObserver);
        if (!isObserver) document.getElementById('killerBoardUndoBtn').addEventListener('click', undoLastDart);
        // Baseline the pulse-animation tracker at the incoming values rather
        // than wiping it to {} - joining mid-match, already-lost lives
        // shouldn't animate in as if just lost on this first paint. Only
        // done on this first arrival at the board stage - every later
        // setState() call in this same match leaves renderedRemaining alone
        // so it keeps comparing against the truly-last-rendered values (see
        // livesDotsHTML) instead of resetting the comparison to 0 on every
        // single sync update, which animated every dot on every update.
        renderedRemaining = {};
        currentPlayers.forEach(p => { renderedRemaining[p] = snapshot.state[p].remaining; });
    }
    state = snapshot.state;
    activeIndex = snapshot.activeIndex;
    dartsThisTurn = snapshot.dartsThisTurn;
    turnDarts = snapshot.turnDarts;
    lastTurnDarts = snapshot.lastTurnDarts;
    matchComplete = snapshot.matchComplete;
    if (snapshot.lastToast && snapshot.lastToast.seq !== lastAppliedToastSeq) {
        lastAppliedToastSeq = snapshot.lastToast.seq;
        renderToast(snapshot.lastToast.msg, snapshot.lastToast.kind);
    } else {
        hideToast();
    }
    renderAll();
    // See x01-game-controller.js's setState for why this matters - a client
    // that only receives a finished match over sync still needs this to
    // reach app.js's handleGameChange and arm the victory modal.
    notifyChange();
}

function mount(container_, { players, gameId, onChange, lives: livesOption, includeBull: includeBullOption, observer = false }) {
    container = container_;
    currentPlayers = players;
    onChangeCallback = onChange;
    lives = livesOption || configRowDefault(killer, 'killer-lives');
    includeBull = includeBullOption || configRowDefault(killer, 'killer-include-bull');
    isObserver = observer;
    currentStage = 'assign';
    toastSeq = 0;
    lastToast = null;
    lastAppliedToastSeq = 0;

    assignments = {};
    assignHistory = [];
    assignIndex = 0;

    container.innerHTML = assignScreenHTML(players, isObserver);
    if (!isObserver) {
        document.getElementById('killerAssignUndoBtn').addEventListener('click', handleAssignUndo);
        document.getElementById('killerAssignStartBtn').addEventListener('click', beginGamePhase);
    }
    renderAssignScreen();
}

export default { definition: killer, mount, hasProgress, getState, setState };
